import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Truck as TruckIcon, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge, STATUS_META } from "../components/StatusBadge";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

const TYRE_COUNTS = [4, 6, 10, 12, 14];
const EMPTY = { plat_nomor: "", brand: "", model: "", num_tyres: 10, notes: "" };

export default function TrucksPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role === "management" || user?.role === "admin";
  const [trucks, setTrucks] = useState([]);
  const [maintIds, setMaintIds] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // truck object or null
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/trucks").then((r) => setTrucks(r.data)).catch(() => {});
    api.get("/fleet-status").then((r) => {
      const ids = new Set(r.data.maintenance_units.filter((u) => u.vehicle_type === "truck").map((u) => u.vehicle_id));
      setMaintIds(ids);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 30000);

  const effectiveStatus = (t) => maintIds.has(t.id) ? "maintenance" : (t.manual_status || "siap_jalan");

  const setStatus = async (t, newStatus, e) => {
    e?.stopPropagation();
    if (newStatus === "maintenance") {
      toast.info("Status Maintenance otomatis aktif ketika ada log berjalan di menu Logbook.");
      return;
    }
    try {
      await api.patch(`/trucks/${t.id}/status`, { manual_status: newStatus });
      toast.success(`Status diubah: ${STATUS_META[newStatus]?.label}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t, e) => {
    e?.stopPropagation();
    setEditing(t);
    setForm({ plat_nomor: t.plat_nomor, brand: t.brand || "", model: t.model || "", num_tyres: t.num_tyres, notes: t.notes || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/trucks/${editing.id}`, form);
        toast.success("Truck diperbarui");
      } else {
        await api.post("/trucks", form);
        toast.success("Truck berhasil ditambahkan");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (t, e) => {
    e?.stopPropagation();
    if (!confirm(`Hapus truck ${t.plat_nomor}? Aksi ini tidak dapat dibatalkan.`)) return;
    try {
      await api.delete(`/trucks/${t.id}`);
      toast.success("Truck dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Head Truck</h1>
          <p className="text-slate-500 text-sm mt-1">Kelola head truck berdasarkan plat nomor.</p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase tracking-wider rounded-sm btn-industrial h-12"
          data-testid="add-truck-btn"
        >
          <Plus className="w-5 h-5 mr-1" /> Tambah Truck
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {trucks.map((t) => (
          <div
            key={t.id}
            className="group border-2 border-slate-200 hover:border-slate-900 bg-white p-5 rounded-sm transition-colors"
            data-testid={`truck-card-${t.plat_nomor}`}
          >
            <div
              onClick={() => nav(`/vehicles/truck/${t.id}`)}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Plat Nomor</div>
                  <div className="font-mono text-2xl font-bold text-slate-900 mt-1">{t.plat_nomor}</div>
                  <div className="text-sm text-slate-600 mt-1">{t.brand} {t.model}</div>
                  <div className="mt-2">
                    <StatusBadge status={effectiveStatus(t)} testId={`truck-status-${t.plat_nomor}`} />
                  </div>
                </div>
                <div className="w-10 h-10 bg-slate-900 group-hover:bg-orange-500 flex items-center justify-center transition-colors">
                  <TruckIcon className="w-5 h-5 text-orange-400 group-hover:text-slate-900" />
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                  {t.num_tyres} ban · {(t.current_km || 0).toLocaleString()} km
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-orange-500" />
              </div>
            </div>
            <div className="flex gap-1 mt-3 pt-3 border-t border-slate-100">
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => openEdit(t, e)}
                data-testid={`edit-truck-${t.plat_nomor}`}
                className="flex-1 rounded-sm border-slate-300 hover:border-slate-900"
              >
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => del(t, e)}
                  data-testid={`delete-truck-${t.plat_nomor}`}
                  className="rounded-sm border-red-300 hover:border-red-600 hover:bg-red-50 text-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
            {!maintIds.has(t.id) && (
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                <Select value={t.manual_status || "siap_jalan"} onValueChange={(v) => setStatus(t, v)}>
                  <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`truck-status-select-${t.plat_nomor}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="siap_jalan">✅ Siap Jalan</SelectItem>
                    <SelectItem value="rusak">⚠ Rusak</SelectItem>
                    <SelectItem value="non_aktif">⏻ Non-Aktif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        ))}
        {trucks.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-12 border-2 border-dashed border-slate-300 rounded-sm">
            Belum ada truck. Klik <b>Tambah Truck</b> untuk mulai.
          </div>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm border-2 border-slate-900">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">
              {editing ? "Edit Head Truck" : "Tambah Head Truck"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="uppercase text-xs tracking-widest font-bold">Plat Nomor *</Label>
              <Input
                data-testid="truck-plat"
                required
                value={form.plat_nomor}
                onChange={(e) => setForm({ ...form, plat_nomor: e.target.value })}
                className="h-12 rounded-sm font-mono uppercase"
                placeholder="B 1234 XYZ"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Merk</Label>
                <Input data-testid="truck-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="h-12 rounded-sm" placeholder="Hino" />
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Model</Label>
                <Input data-testid="truck-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="h-12 rounded-sm" placeholder="FM 260 JD" />
              </div>
            </div>
            <div>
              <Label className="uppercase text-xs tracking-widest font-bold">Jumlah Ban *</Label>
              <Select value={String(form.num_tyres)} onValueChange={(v) => setForm({ ...form, num_tyres: Number(v) })}>
                <SelectTrigger className="h-12 rounded-sm" data-testid="truck-num-tyres"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYRE_COUNTS.map((n) => <SelectItem key={n} value={String(n)}>{n} ban</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="truck-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                {busy ? "Menyimpan..." : editing ? "Simpan Perubahan" : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
